import { useEffect } from "react";
import { getFacilityScope } from "./facilityScope";
import { isEmbedded } from "./embed";

// Auto-expire an authenticated session after this much inactivity.
export const SESSION_TIMEOUT_MS = 60 * 60 * 1000; // 1 hour
const ACTIVITY_KEY = "pc_last_activity";

// Keys we wipe on a full session clear (logout / timeout).
const CLEAR_KEYS = [
  "token", "idToken", "refreshToken", "userLoggedIn", "userRegistered",
  "phoneNumber", "intendedPath", "userChecked", "appInitialized", "appVisited",
  "selectedFacilityId", "hospitalSelected", "fromQR", "otpVerificationId",
  "signupCompleted", "specializationSelected", "fromSpecializationQR",
  "appointment-storage", "facility-store", "auth-store", "recentSearches",
  ACTIVITY_KEY,
];

// Wipe app storage. With keepScope, the facility scope + embed flag survive so a
// re-login inside a partner embed stays scoped to that hospital.
export const clearSession = (opts?: { keepScope?: boolean }): void => {
  const scopeKeys = ["selectedFacilityId", "hospitalSelected", "fromQR"];
  const keys = opts?.keepScope
    ? CLEAR_KEYS.filter((k) => !scopeKeys.includes(k))
    : CLEAR_KEYS;
  keys.forEach((k) => {
    try { localStorage.removeItem(k); } catch { /* ignore */ }
  });

  if (opts?.keepScope) {
    try {
      const preserve: Record<string, string | null> = {
        pc_facilityScope: sessionStorage.getItem("pc_facilityScope"),
        qrFacilityId: sessionStorage.getItem("qrFacilityId"),
        pc_embed: sessionStorage.getItem("pc_embed"),
        isLoggingOut: sessionStorage.getItem("isLoggingOut"),
      };
      sessionStorage.clear();
      Object.entries(preserve).forEach(([k, v]) => {
        if (v !== null) sessionStorage.setItem(k, v);
      });
    } catch { /* ignore */ }
  } else {
    try { sessionStorage.clear(); } catch { /* ignore */ }
  }
};

export const touchActivity = (): void => {
  try {
    if (localStorage.getItem("token")) {
      localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
    }
  } catch { /* ignore */ }
};

const isExpired = (): boolean => {
  try {
    if (!localStorage.getItem("token")) return false; // only authed sessions expire
    const last = Number(localStorage.getItem(ACTIVITY_KEY) || 0);
    return last > 0 && Date.now() - last > SESSION_TIMEOUT_MS;
  } catch {
    return false;
  }
};

// On timeout: remember how to re-enter (embedded + which hospital), wipe, then
// send the user back to a fresh start so nothing stale leaks into the next session.
const expireNow = (): void => {
  const embedded = isEmbedded();
  const scopedId = getFacilityScope()?.facilityId;
  clearSession();
  if (embedded && scopedId) {
    window.location.replace(`/hospital/${scopedId}?embed=1`);
  } else {
    window.location.replace("/get-user");
  }
};

/**
 * Expire an authenticated session after SESSION_TIMEOUT_MS of inactivity, and
 * clear all storage when it happens. No-op for unauthenticated visitors.
 */
export const useSessionExpiry = (): void => {
  useEffect(() => {
    // Seed activity on an authed session that has none yet.
    try {
      if (localStorage.getItem("token") && !localStorage.getItem(ACTIVITY_KEY)) {
        localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
      }
    } catch { /* ignore */ }

    if (isExpired()) {
      expireNow();
      return;
    }

    let lastTouch = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - lastTouch > 30000) { // throttle writes to every 30s
        lastTouch = now;
        touchActivity();
      }
    };
    const events = ["mousedown", "keydown", "touchstart", "scroll"];
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));

    const interval = window.setInterval(() => {
      // Seed activity for a session that logged in after mount.
      try {
        if (localStorage.getItem("token") && !localStorage.getItem(ACTIVITY_KEY)) {
          localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
        }
      } catch { /* ignore */ }
      if (isExpired()) expireNow();
    }, 60000);

    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity));
      window.clearInterval(interval);
    };
  }, []);
};
